import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Database } from "lucide-react";

export const QuotaWidget = ({ compact = false, refreshKey = 0 }) => {
  const [q, setQ] = useState(null);

  useEffect(() => {
    axios.get(`${API}/quota`).then((r) => setQ(r.data)).catch(() => {});
  }, [refreshKey]);

  if (!q) return null;
  const tone = q.critical ? "text-[#7A4B3A]" : q.warning ? "text-[#6B5B33]" : "text-brand-moss";

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-brand-subtle" data-testid="quota-compact">
        {(q.warning || q.critical) && <AlertTriangle className={`h-3.5 w-3.5 ${tone}`} strokeWidth={1.75} />}
        <span>Google Places</span>
        <span className={tone}>{q.today}/{q.daily_limit}</span>
        <span className="text-brand-line">|</span>
        <span>{q.month}/{q.monthly_limit}</span>
      </div>
    );
  }

  const Bar = ({ pct }) => (
    <div className="h-[3px] bg-brand-line overflow-hidden mt-2">
      <div className={`h-full ${q.critical ? "bg-[#A9705C]" : q.warning ? "bg-[#B79A4B]" : "bg-brand-moss"}`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );

  return (
    <Card className="p-5 sm:p-6 bg-white border-brand-line rounded-md shadow-card" data-testid="quota-widget">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="eyebrow">Quota Google Places</div>
          <div className="font-display text-xl sm:text-2xl text-brand-ink mt-2">Consommation API</div>
        </div>
        {(q.warning || q.critical) && (
          <div className={`flex items-center gap-2 text-xs ${tone}`} data-testid="quota-alert">
            <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
            {q.critical ? "Plafond presque atteint" : "Approche de la limite"}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <div className="flex items-baseline justify-between">
            <span className="eyebrow">Aujourd'hui</span>
            <span className="font-mono text-sm text-brand-ink" data-testid="quota-today">{q.today} / {q.daily_limit}</span>
          </div>
          <Bar pct={q.daily_pct} />
          <div className="mt-2 text-[11px] text-brand-subtle font-mono">{q.daily_pct}% utilisé · {q.daily_remaining} restants</div>
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <span className="eyebrow">Ce mois ({q.month_label})</span>
            <span className="font-mono text-sm text-brand-ink" data-testid="quota-month">{q.month} / {q.monthly_limit}</span>
          </div>
          <Bar pct={q.monthly_pct} />
          <div className="mt-2 text-[11px] text-brand-subtle font-mono">{q.monthly_pct}% utilisé · {q.monthly_remaining} restants</div>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-brand-line flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-mono text-brand-subtle">
        <span className="flex items-center gap-2"><Database className="h-3.5 w-3.5" strokeWidth={1.75} /> Cache : {q.cache.cached_places} établissements</span>
        <span className="text-brand-moss">{q.cache.calls_saved} appels économisés</span>
        <span>TTL {q.cache.ttl_days} jours</span>
      </div>
    </Card>
  );
};
